import type { PluginImportConfig } from '@squaredup/plugins';
import { getTimeframeLabel, type TimeframeEnumValue } from '@squaredup/timeframes';
import LoadingSpinner from 'components/LoadingSpinner';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import { useEffect, useState } from 'react';
import { useDatasetContext } from 'ui/editor/dataStream/contexts/DatasetContext';
import { EditorSteps } from 'ui/editor/dataStream/TileEditor/constants';
import TileEditorStepsContext from 'ui/editor/dataStream/TileEditor/contexts/TileEditorStepsContext';
import { useDataStreamFilters } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamFilters';
import { useDataStreamSupportedTimeframes } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamSupportedTimeframes';
import { useDataStreamType } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamType';
import { StepWithCondition, type TileEditorStepsValue } from 'ui/editor/dataStream/TileEditor/hooks/useTileEditorSteps';
import { useTileEditorStore } from 'ui/editor/dataStream/TileEditor/state/TileEditorStoreProvider';
import { DataStreamConfigurationStep } from 'ui/editor/dataStream/TileEditor/steps/DataStreamConfiguration/DataStreamConfigurationStep';
import { DataStreamConfigurationStepSubLabel } from 'ui/editor/dataStream/TileEditor/steps/DataStreamConfiguration/DataStreamConfigurationStepSubLabel';
import { DataStreamDisplayName } from 'ui/editor/dataStream/TileEditor/steps/DataStreamDisplayName';
import { DataStreamMetadata } from 'ui/editor/dataStream/TileEditor/steps/DataStreamMetadata';
import { DataStreamObjectsSublabel } from 'ui/editor/dataStream/TileEditor/steps/DataStreamObjectsSublabel';
import { DataStreamShapingLabels } from 'ui/editor/dataStream/TileEditor/steps/DataStreamShapingLabels';
import { countModifiedColumns } from 'ui/editor/dataStream/TileEditor/steps/MetadataEditor/MetadataEditorTileState';
import { ObjectsStep } from 'ui/editor/dataStream/TileEditor/steps/ObjectsStep';
import { ShapingStep } from 'ui/editor/dataStream/TileEditor/steps/ShapingStep';
import { TimeframeStep } from 'ui/editor/dataStream/TileEditor/steps/TimeframeStep';
import { PluginImportConfigDataStreamStep } from './PluginImportConfigDataStreamStep';
import { usePluginImportConfigEditorContext } from './PluginImportConfigEditorContext';
import { PluginImportConfigMappingStep } from './PluginImportConfigMappingStep';

const getTimeframeSteplabel = (
    supportedTimeframes: boolean | TimeframeEnumValue[],
    configTimeframe: TimeframeEnumValue
) => {
    if (supportedTimeframes === false) {
        return 'Not supported';
    }

    if (configTimeframe === 'none') {
        return 'None';
    }
    return `${getTimeframeLabel(configTimeframe)} (Fixed)`;
};

interface PluginImportConfigStepsContextWrapperProps {
    initialStep?: EditorSteps;
    dataSourceId: string;
}
export const PluginImportConfigStepsContextWrapper: React.FC<PluginImportConfigStepsContextWrapperProps> = ({
    initialStep,
    dataSourceId,
    children
}) => {
    const { config: datasetConfig, setConfig: setDatasetConfig } = useDatasetContext();
    const { data: datasetData, isConfigured } = useDataStreamConfig(datasetConfig);
    const supportedTimeframes = useDataStreamSupportedTimeframes(datasetConfig.dataStream?.id);
    const { dataStreamType, isLoading } = useDataStreamType(datasetConfig);

    const { dataStreamState, scope, storeCurrentStep, workspaceId, dispatch } = useTileEditorStore((s) => ({
        storeCurrentStep: s.currentStep,
        dataStreamState: s.dataStream,
        scope: s.scope,
        workspaceId: s.dashboard.workspaceId,
        dispatch: s.dispatch
    }));

    const [previousDataStreamId, setPreviousDataStreamId] = useState<string | undefined>(
        dataStreamState.selectedDataStream?.id
    );
    const { setConfig, configId, tileConfig, ...mappingProps } = usePluginImportConfigEditorContext();

    useEffect(() => {
        // Reset the mapping fields when the selected data stream changes.
        // This prevents the user saving the old mapping values which are likely now invalid.
        if (dataStreamState.selectedDataStream?.id !== previousDataStreamId) {
            setPreviousDataStreamId(dataStreamState.selectedDataStream?.id);
            setConfig(
                (prev): PluginImportConfig => ({
                    ...prev,
                    sourceNameColumn: undefined,
                    sourceIdColumn: undefined,
                    sourceType: {
                        mode: 'fixed',
                        value: undefined,
                        column: undefined
                    },
                    columnMapping: {}
                })
            );
        }
    }, [dataStreamState.selectedDataStream?.id, mappingProps, previousDataStreamId, setConfig]);

    const dataStreamFilters = useDataStreamFilters({
        dispatch,
        ...dataStreamState,
        filters: {
            ...dataStreamState.filters,
            dataSourceId: dataSourceId
        }
    });

    const hasDataStream = Boolean(datasetConfig.dataStream);
    const isConfigurableDataStream = dataStreamType && dataStreamType.isConfigurable;

    const hasScope = Object.keys(datasetConfig.scope || {}).length > 0;

    const editorSteps: StepWithCondition[] = [
        {
            name: EditorSteps.dataStream,
            isEnabled: !isLoading,
            sublabel: <DataStreamDisplayName />,
            component: (
                <PluginImportConfigDataStreamStep dataStreamFilters={dataStreamFilters} pluginId={dataSourceId} />
            )
        },
        {
            name: EditorSteps.objects,
            isEnabled: false,
            sublabel: !hasDataStream ? (
                '-'
            ) : storeCurrentStep !== EditorSteps.objects && !datasetConfig.scope ? (
                <LoadingSpinner size={24} />
            ) : (
                <DataStreamObjectsSublabel
                    scopeState={scope}
                    scopeConfig={datasetConfig.scope ?? []}
                    workspaceId={workspaceId}
                />
            ),
            component: <ObjectsStep />
        },
        {
            name: EditorSteps.parameters,
            isEnabled:
                !isLoading && hasDataStream && isConfigurableDataStream && (hasScope || !dataStreamType.supportsScope),
            sublabel: isConfigurableDataStream ? <DataStreamConfigurationStepSubLabel /> : '-',
            component: <DataStreamConfigurationStep />
        },
        {
            name: EditorSteps.timeframe,
            isEnabled: !isLoading && hasDataStream && supportedTimeframes !== false && isConfigured,
            sublabel: getTimeframeSteplabel(supportedTimeframes, 'last24hours'),
            component: <TimeframeStep />
        },
        {
            name: EditorSteps.shaping,
            isEnabled: !isLoading && hasDataStream && isConfigured,
            sublabel: <DataStreamShapingLabels />,
            component: <ShapingStep />
        },
        {
            name: EditorSteps.metadata,
            isEnabled: !isLoading && hasDataStream && isConfigured,
            sublabel: (() => {
                const numberOfModifiedColumns = countModifiedColumns(
                    datasetConfig.dataStream?.metadata,
                    datasetData.metadata.columns.map((c) => c.name)
                );

                return numberOfModifiedColumns > 0 ? `${numberOfModifiedColumns} configured` : '';
            })(),
            component: <DataStreamMetadata config={datasetConfig} setConfig={setDatasetConfig} />
        },
        {
            name: EditorSteps.pluginConfigMapping,
            isEnabled: !isLoading && hasDataStream && isConfigured,
            sublabel: null,
            component: <PluginImportConfigMappingStep />
        }
    ];

    const [currentStepName, setCurrentStepName] = useState(initialStep);

    const availableSteps = editorSteps.filter((step) => step.isEnabled);
    const stepInFlow = availableSteps.findIndex((step) => step.name === currentStepName);

    const canMoveToNextStep = stepInFlow < availableSteps.length - 1;

    const nextStep = () => {
        if (canMoveToNextStep) {
            setCurrentStepName(availableSteps[stepInFlow + 1]?.name);
        }
    };

    const handleSetEditorStepName = (stepName?: EditorSteps | undefined, force = false) => {
        if (force) {
            // If we're forcing a step we don't want to do anything other than set the current editor step
            return setCurrentStepName(stepName);
        }
        const newCurrentStep = editorSteps.find((s) => s.name === stepName);

        if (newCurrentStep && newCurrentStep?.isEnabled === false) {
            return;
        }

        return setCurrentStepName(newCurrentStep?.name);
    };

    const stepsValue: TileEditorStepsValue = {
        currentEditorStep: currentStepName,
        isConfiguredAfterCurrentStep: false,
        editorSteps: availableSteps,
        canMoveToNextStep,
        isConfigured: false,
        isDocked: true,
        canDock: false,
        setCurrentEditorStep: handleSetEditorStepName,
        nextStep
    };

    return <TileEditorStepsContext.Provider value={stepsValue}>{children}</TileEditorStepsContext.Provider>;
};
